import { useScienceTutorSection } from './scienceTutorContext';
import { useState, type ComponentType } from 'react';

import { formatFormula } from './polymersBiomolecules';
import {
  AMINO_ACIDS,
  BIOMOLECULE_QUESTIONS,
  BIOMOLECULE_TOPICS,
  buildPeptide,
  buildTriglyceride,
  DISACCHARIDES,
  FATTY_ACIDS,
  PEPTIDE_MAX_LENGTH,
  PEPTIDE_MIN_LENGTH,
  POLYSACCHARIDES,
  PROTEIN_LEVELS,
  type BiomoleculeTopic,
  type PeptideResult,
} from './polymersBiomoleculesBio';
import { ChoicePanel, FeedbackBox, SegmentFormula } from './PolymersShared';
import { CARD_CLASS, chipClass } from './polymersStyles';

const WATER_PREDICTIONS = [0, 1, 2, 3];

function PeptideBuilder() {
  const [sequence, setSequence] = useState<string[]>([]);
  const [waterPrediction, setWaterPrediction] = useState<number | null>(null);
  useScienceTutorSection('PeptideBuilder', { sequence, waterPrediction });
  const [result, setResult] = useState<PeptideResult | null>(null);

  const update = (next: string[]) => {
    setSequence(next);
    setResult(null);
  };
  const peptide = result?.ok ? result.peptide : null;

  return (
    <section className={CARD_CLASS} aria-labelledby="peptide-builder-heading">
      <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
        Peptide builder
      </p>
      <h3 id="peptide-builder-heading" className="mt-2 text-xl font-extrabold text-ink">
        Join {PEPTIDE_MIN_LENGTH} or {PEPTIDE_MAX_LENGTH} amino acids
      </h3>
      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4" role="group" aria-label="Amino acids">
        {AMINO_ACIDS.map((acid) => (
          <button
            key={acid.id}
            type="button"
            disabled={sequence.length >= PEPTIDE_MAX_LENGTH}
            onClick={() => update([...sequence, acid.id])}
            aria-label={`Add ${acid.name}`}
            className={`${chipClass(false)} disabled:cursor-not-allowed disabled:opacity-40`}
          >
            <span className="block">{acid.code}</span>
            <span className="block font-mono text-xs text-slate2">R = {acid.rGroup}</span>
          </button>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <p className="text-sm font-extrabold text-ink">
          Sequence:{' '}
          {sequence.length === 0
            ? 'none yet'
            : sequence.map((id) => AMINO_ACIDS.find((acid) => acid.id === id)!.code).join(' + ')}
        </p>
        <button
          type="button"
          onClick={() => update([])}
          className="rounded-full border border-hairline bg-surface px-3 py-1 text-xs font-extrabold text-ink"
        >
          Clear
        </button>
      </div>
      <fieldset className="mt-4">
        <legend className="text-sm font-extrabold text-ink">
          Predict: how many H₂O are released?
        </legend>
        <div className="mt-2 flex flex-wrap gap-2">
          {WATER_PREDICTIONS.map((count) => (
            <button
              key={count}
              type="button"
              aria-pressed={waterPrediction === count}
              aria-label={`${count} water molecules`}
              onClick={() => {
                setWaterPrediction(count);
                setResult(null);
              }}
              className={`h-11 min-w-11 rounded-full border px-3 font-extrabold ${
                waterPrediction === count
                  ? 'border-ink bg-ink text-white'
                  : 'border-hairline bg-surface text-ink'
              }`}
            >
              {count}
            </button>
          ))}
        </div>
      </fieldset>
      <button
        type="button"
        disabled={waterPrediction === null || sequence.length === 0}
        onClick={() => setResult(buildPeptide(sequence))}
        className="btn-pill-primary mt-4 disabled:cursor-not-allowed disabled:opacity-40"
      >
        Join with peptide bonds
      </button>
      {result && !result.ok ? (
        <div className="mt-4">
          <FeedbackBox correct={false} title="Cannot build this peptide">
            {result.message}
          </FeedbackBox>
        </div>
      ) : null}
      {peptide && waterPrediction !== null ? (
        <div className="mt-4 space-y-3">
          <FeedbackBox correct={waterPrediction === peptide.waterReleased}>
            {waterPrediction === peptide.waterReleased
              ? `${peptide.sequence.length} amino acids form ${peptide.peptideBonds} peptide bond${peptide.peptideBonds === 1 ? '' : 's'}, and each bond releases one H₂O.`
              : `You predicted ${waterPrediction}. A ${peptide.kind} has ${peptide.peptideBonds} peptide bond${peptide.peptideBonds === 1 ? '' : 's'} (one fewer than the number of amino acids), and each bond releases one H₂O, so ${peptide.waterReleased} H₂O form.`}
          </FeedbackBox>
          <div className="rounded-2xl bg-wash-mint p-4">
            <p className="text-xs font-extrabold uppercase tracking-wide text-slate2">
              {peptide.kind} {peptide.name} · {peptide.formula}
            </p>
            <p className="mt-2 text-base">
              <SegmentFormula segments={peptide.segments} />
            </p>
            <ul className="mt-3 space-y-1 text-sm font-medium leading-6 text-slate2">
              {peptide.sequence.map((acid, index) => (
                <li key={`${acid.id}-${index}`}>
                  <span className="font-extrabold text-ink">{acid.name}</span>
                  {` (${acid.sideChainClass} R group): ${acid.note}`}
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function ProteinLevelsModel() {
  return (
    <ol className="space-y-3">
      {PROTEIN_LEVELS.map((level, index) => (
        <li
          key={level.id}
          className="grid gap-2 rounded-2xl border border-hairline bg-surface p-4 sm:grid-cols-[auto_minmax(0,1fr)]"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-ink text-sm font-extrabold text-white">
            {index + 1}
          </span>
          <div>
            <p className="font-extrabold text-ink">{level.name} structure</p>
            <p className="text-sm font-medium leading-6 text-slate2">{level.whatItIs}</p>
            <p className="mt-1 text-sm font-bold leading-6 text-ink">Held by: {level.heldBy}</p>
            <p className="text-xs font-bold text-slate2">Example: {level.example}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function CarbohydrateModel() {
  return (
    <div className="space-y-3">
      {DISACCHARIDES.map((sugar) => (
        <div key={sugar.id} className="rounded-2xl border border-hairline bg-surface p-4">
          <p className="break-words font-mono font-extrabold text-ink">
            {sugar.reactants[0]} + {sugar.reactants[1]} → {sugar.product} + H₂O
          </p>
          <p className="mt-1 break-words font-mono text-sm font-bold text-slate2">
            {formatFormula(sugar.reactantCounts[0])} + {formatFormula(sugar.reactantCounts[1])} →{' '}
            {formatFormula(sugar.productCounts)} + H₂O · {sugar.linkDetail}
          </p>
        </div>
      ))}
      <div className="grid gap-3 md:grid-cols-2">
        {POLYSACCHARIDES.map((polymer) => (
          <div key={polymer.id} className="rounded-2xl border border-hairline bg-surface p-4">
            <p className="font-extrabold text-ink">
              {polymer.name}: polymer of {polymer.monomer}
            </p>
            <p className="mt-1 font-mono text-sm font-bold text-ink">
              [–{formatFormula(polymer.residueCounts)}–]ₙ
            </p>
            <p className="mt-1 text-sm font-medium leading-6 text-slate2">
              {polymer.link}. {polymer.role}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function LipidModel() {
  const [fattyAcidId, setFattyAcidId] = useState(FATTY_ACIDS[1].id);
  useScienceTutorSection('LipidModel', { fattyAcidId });
  const triglyceride = buildTriglyceride(fattyAcidId);
  return (
    <div className="space-y-3">
      <div className="grid gap-2 sm:grid-cols-2" role="group" aria-label="Fatty acid">
        {FATTY_ACIDS.map((acid) => (
          <button
            key={acid.id}
            type="button"
            aria-pressed={fattyAcidId === acid.id}
            onClick={() => setFattyAcidId(acid.id)}
            className={chipClass(fattyAcidId === acid.id)}
          >
            {acid.name}
            <span className="block text-xs opacity-75">
              {acid.doubleBonds} C=C · {formatFormula(acid.counts)}
            </span>
          </button>
        ))}
      </div>
      {triglyceride ? (
        <div className="rounded-2xl border border-hairline bg-surface p-4">
          <p className="break-words font-mono text-sm font-bold text-slate2">
            glycerol + 3 {triglyceride.fattyAcid.name.split(' (')[0]} → {triglyceride.name} + 3 H₂O
          </p>
          <div className="mt-3 space-y-1 text-base" aria-label={`${triglyceride.name} structure`}>
            {triglyceride.lines.map((line, index) => (
              <p key={index}>
                <SegmentFormula segments={line} />
              </p>
            ))}
          </div>
          <p className="mt-3 text-sm font-bold leading-6 text-ink">
            {triglyceride.formula} · {triglyceride.saturation} ·{' '}
            {triglyceride.fattyAcid.triglycerideState} at 25 °C
          </p>
          <p className="text-sm font-medium leading-6 text-slate2">
            {triglyceride.saturation === 'saturated'
              ? 'Straight saturated chains pack closely, so dispersion forces are stronger.'
              : 'Each cis C=C kinks the chain, so chains pack loosely and dispersion forces are weaker.'}
          </p>
        </div>
      ) : null}
    </div>
  );
}

const TOPIC_MODELS: Record<Exclude<BiomoleculeTopic, 'proteins'>, ComponentType> = {
  'protein-structure': ProteinLevelsModel,
  carbohydrates: CarbohydrateModel,
  lipids: LipidModel,
};

export function PolymersBiomoleculesMode() {
  const [topic, setTopic] = useState<BiomoleculeTopic>('proteins');
  const [questionIndex, setQuestionIndex] = useState(0);
  const [revealedTopics, setRevealedTopics] = useState<BiomoleculeTopic[]>([]);
  useScienceTutorSection('PolymersBiomoleculesMode', { topic, questionIndex, revealedTopics });
  const questions = BIOMOLECULE_QUESTIONS.filter((question) => question.topic === topic);
  const question = questions[Math.min(questionIndex, questions.length - 1)];
  const TopicModel = topic === 'proteins' ? null : TOPIC_MODELS[topic];
  const revealed = revealedTopics.includes(topic);

  return (
    <div className="space-y-6">
      <section className={CARD_CLASS}>
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
          1 · Choose a biomolecule family
        </p>
        <h2 className="mt-2 text-2xl font-extrabold text-ink">Biomolecules</h2>
        <div className="mt-4 grid grid-cols-2 gap-2 lg:grid-cols-4" role="group" aria-label="Topic">
          {BIOMOLECULE_TOPICS.map((option) => (
            <button
              key={option.id}
              type="button"
              aria-pressed={topic === option.id}
              onClick={() => {
                setTopic(option.id);
                setQuestionIndex(0);
              }}
              className={chipClass(topic === option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-2">
        <section className={CARD_CLASS}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
              2 · Predict
            </p>
            <div className="flex gap-2" role="group" aria-label="Question">
              {questions.map((item, index) => (
                <button
                  key={item.id}
                  type="button"
                  aria-pressed={item.id === question.id}
                  aria-label={`Question ${index + 1}`}
                  onClick={() => setQuestionIndex(index)}
                  className={`h-9 min-w-9 rounded-full border px-3 text-sm font-extrabold ${
                    item.id === question.id
                      ? 'border-ink bg-ink text-white'
                      : 'border-hairline bg-surface text-ink'
                  }`}
                >
                  {index + 1}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-3">
            <ChoicePanel
              key={question.id}
              question={question}
              onChecked={() =>
                setRevealedTopics((current) =>
                  current.includes(topic) ? current : [...current, topic],
                )
              }
            />
          </div>
        </section>

        {topic === 'proteins' ? (
          <PeptideBuilder />
        ) : (
          <section className={CARD_CLASS}>
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-mint">
              3 · Model
            </p>
            <div className="mt-3">
              {revealed ? (
                TopicModel && <TopicModel />
              ) : (
                <p className="rounded-2xl bg-wash-sky p-4 text-sm font-bold leading-6 text-slate2">
                  Check an answer first. The model for this family appears after your prediction.
                </p>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
